import { defineFeature } from '../define-feature';
import { TEST_IDS, fieldTestId, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'marketing.communication.settings',
  category: 'marketing',
  title: 'Paramètres de communication (expéditeurs, SMS)',
  intent:
    "Régler, avant toute campagne, le nom d'expéditeur affiché à vos clients (SMS et e-mail) et les pays autorisés pour l'envoi de SMS, afin de maîtriser vos coûts.",
  context: {
    why: "Avant d'envoyer quoi que ce soit, deux réglages conditionnent la réussite et le coût de vos communications. Le nom d'expéditeur, d'abord : c'est ce que votre client voit en recevant votre e-mail ou votre SMS. Un nom reconnaissable — celui de votre établissement — rassure et fait ouvrir le message ; un expéditeur obscur finit ignoré ou en spam. Les pays autorisés pour le SMS, ensuite : le SMS est facturé à l'unité, et le tarif varie énormément d'un pays à l'autre. Limiter l'envoi aux pays de vos clients vous protège de mauvaises surprises de facturation. Ces réglages se posent une fois, puis s'appliquent à toutes vos campagnes et à vos messages automatiques.",
    use_cases: [
      'Mettre le nom de votre établissement comme expéditeur des e-mails et des SMS.',
      "N'autoriser que la France (ou vos pays clients) pour l'envoi de SMS.",
      'Consulter et accepter les tarifs SMS avant de lancer une première campagne.',
    ],
  },
  required_scopes: ['establishment.info.update', 'campaign.sms.read'],
  ui_entry_points: [
    { route: '/marketing/campaigns', element: 'Bouton « Paramètres » en haut de la liste des campagnes' },
  ],
  api: {
    primary_operation_id: 'ProEstablishmentAppController_upsertProProfile',
    related_operation_ids: [
      'SmsAppController_getSmsPrices',
      'SmsAppController_putSmsPricesAccepted',
      'SmsAppController_estimateSmsCampaignCost',
      // Expéditeur sur le domaine du pro (section « Adresse d'expédition de vos e-mails »).
      'CampaignSenderDomainAppController_getSettings',
      'CampaignSenderDomainAppController_create',
      'CampaignSenderDomainAppController_remove',
      'CampaignSenderDomainAppController_setSenderEmail',
    ],
  },
  steps: [
    '**Ouvrez « Paramètres »** depuis Communication, en haut de la liste des campagnes.',
    "**Réglez le nom d'expéditeur de vos campagnes SMS** : c'est le nom qui s'affiche à vos clients comme expéditeur de vos campagnes. Laissé vide, c'est le nom de votre établissement qui est utilisé.",
    "**Réglez le nom d'expéditeur e-mail** : le nom affiché sur vos e-mails (relances, confirmations, mailing) — en général le nom de votre établissement.",
    "**Choisissez l'adresse d'expédition de vos campagnes e-mail** : l'adresse Propelsia par défaut, ou votre propre domaine (option Marque blanche, domaine acheté ou transféré chez nous). Dans ce cas, choisissez le domaine et saisissez l'adresse (par exemple « contact »), puis enregistrez : nous activons le domaine pour vous, sans manipulation de votre côté.",
    "**Consultez et acceptez les tarifs SMS**, puis choisissez les pays autorisés à l'envoi. Par défaut, seul le pays de votre entreprise est autorisé.",
    "**Enregistrez** : ces réglages s'appliquent à toutes vos communications à venir.",
  ],
  gotchas: [
    "**À régler AVANT de créer ou d'envoyer une campagne.** L'expéditeur et les tarifs SMS conditionnent l'envoi : faites ce réglage une fois au départ, vous n'aurez plus à y revenir.",
    "**Les SMS sont facturés à l'unité, et certains pays coûtent beaucoup plus cher.** Limiter les pays autorisés est votre garde-fou contre une facture inattendue. Par défaut, seul le pays de votre entreprise est autorisé — n'ajoutez que les pays où vous avez réellement des clients.",
    "**Deux expéditeurs SMS distincts.** Celui de vos campagnes est libre — c'est votre signature commerciale, soignez-le. Celui des messages automatiques (rappels, notifications) reste le nom de la plateforme, et n'est personnalisable qu'avec l'option marque blanche.",
    "**L'expéditeur e-mail couvre relances, confirmations et mailing.** Là aussi un seul nom pour tous vos e-mails : mettez le nom sous lequel vos clients vous connaissent.",
    "**L'activation de votre domaine prend de quelques minutes à quelques heures, et vous n'avez rien à faire.** Nous posons nous-mêmes les enregistrements DNS (signature DKIM, SPF, preuve de propriété). Une campagne e-mail envoyée entre-temps est simplement programmée et part toute seule dès que l'adresse est prête. Si l'activation échouait, nos équipes s'en occupent et vos campagnes partent de votre adresse Propelsia : rien n'est jamais bloqué.",
  ],
  related_features: [
    'marketing.campaign.sms.create',
    'marketing.campaign.email.create',
    'marketing.messages.automatic',
  ],
  demo_script: {
    duration_target_s: 30,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          'Avant toute campagne, réglons ce qui décide du coût et de l’impact de vos messages : l’expéditeur et les SMS.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/marketing'),
        caption: 'Dans le menu latéral, ouvrez « Communication ».',
      },
      { action: 'click', target: sidebarSubTestId('/marketing/campaigns'), caption: 'Puis « Campagne marketing ».' },
      { action: 'wait_for', target: TEST_IDS.comm_settings_open, caption: 'La liste de vos campagnes s’affiche.' },
      {
        action: 'click',
        target: TEST_IDS.comm_settings_open,
        caption: 'Ouvrez les paramètres : c’est ici qu’on pose, une fois pour toutes, les réglages communs.',
      },
      {
        // Expéditeur des SMS de campagne : lettres/chiffres, 3 à 11 caractères (contrainte
        // opérateur). C'est le seul champ expéditeur SMS éditable sans marque blanche —
        // celui des messages automatiques (`sms_sender_name`) y est affiché en lecture seule.
        action: 'fill',
        target: fieldTestId('sms_marketing_sender_name'),
        value: 'MonSalon',
        caption:
          'Le nom d’expéditeur de vos campagnes SMS, c’est ce que votre client voit à réception : un nom reconnaissable rassure.',
        capture: true,
      },
      {
        action: 'fill',
        target: fieldTestId('email_sender_name'),
        value: 'Salon Épure',
        caption: 'Même logique pour l’e-mail : mettez le nom sous lequel vos clients vous connaissent.',
      },
      {
        action: 'click',
        target: TEST_IDS.comm_settings_save,
        caption: 'On enregistre, et ces réglages s’appliquent à toutes vos communications à venir.',
        capture: true,
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Vocal Kaltouma.) Bouton « Paramètres » à côté de « Créer une campagne » : nom d'expéditeur SMS
// > (affiché au client, campagnes + messages auto), nom d'expéditeur e-mail (relances, confirmations,
// > mailing = nom établissement), et payer/accepter les tarifs SMS = pays autorisés (défaut = pays de
// > l'entreprise ; limiter pour éviter les frais élevés, certains pays coûtent beaucoup plus cher).
